// Standard library imports
import React, { useState, useEffect } from 'react';
// External library imports
import { toast } from 'react-toastify';
import moment from 'moment';
import _ from 'lodash';
// Internal module imports
import AfrrClasses from './index.module.css';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import classes from '../../../../styles/AllDevices.module.css';
import { useLoader, useQueryParamWithDefault } from '../../../../hooks';
import { RootAfrrService } from '../../../../services/RootAfrrService';
import { findDiffInSeconds, momentTimeFormater } from '../../../../utils/timeHelper';
import DateRangePicker from '../../../../components/Inputs/DateRangePicker/DateRangePicker';
import { DateParam, StringParam, useQueryParams } from 'use-query-params';
import Table from '../../../../components/Table/Table';
import Typography from '../../../../components/Typography/Typography';
import { UilImport, UilInfoCircle } from '@iconscout/react-unicons';
import IconComponent from '../../../../components/Icon/Icon';
import { DownloadAsExcel } from '../../../../utils/downloadAsExcel';
import { CamelCaseToTextCase } from '../../../../utils/stringHelper';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import MFRRActivationSignalViewer from '../MFRR/ModalComponent/MFRRActivationSignalViewer';
// Css imports
import frrClasses from '../index.module.css';

const AREA = [
    { label: 'DK1', value: 'DK1' },
    { label: 'DK2', value: 'DK2' },
];

export const ActivationEdgeChips = ({ eicCodeOptions, cId }) => {
    const [startLoader, stopLoader] = useLoader();
    const [startDate, setStartDate] = useQueryParamWithDefault('startDateEdge', DateParam, moment().toDate(), cId);
    const [endDate, setEndDate] = useQueryParamWithDefault('endDateEdge', DateParam, moment().add(7, 'days').toDate(), cId);
    const [selectedArea, setSelectedArea] = useQueryParamWithDefault('area', StringParam, AREA[0].value, cId);
    const [selectedEicCode, setSelectedEicCode] = useQueryParamWithDefault('eic', StringParam, eicCodeOptions[0]?.value, cId);
    const [data, setData] = useState({
        activation: [],
        count: 0,
    });
    const [paginationQuery, setPaginationQuery] = useState({
        skip: 0,
        page: 0,
        limit: 15,
    });
    const [signalViewerModal, setSignalViewerModal] = useState({ status: false, data: null });


    const [, setOverviewParams] = useQueryParams({
        FRR_aFRR_date: DateParam,
        FRR_aFRR_startTime: StringParam,
        FRR_aFRR_endTime: StringParam,
        FRR_aFRR_eic: StringParam,
    });

    useEffect(() => {
        if (eicCodeOptions?.length) setSelectedEicCode(selectedEicCode ? selectedEicCode : eicCodeOptions[0]?.value);
    }, [eicCodeOptions]);

    useEffect(() => {
        getActivationEvents();
    }, [paginationQuery.skip, selectedArea]);

    const handlePageChange = (pageNo) => {
        setPaginationQuery((prev) => ({
            ...prev,
            skip: prev.limit * pageNo,
            page: pageNo,
        }));
    };

    const getActivationEvents = (paginated = true) => {
        const params = {
            area: selectedArea,
            startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
            endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : moment().add(7, 'days').format('YYYY-MM-DD'),
            skip: paginationQuery.skip,
            limit: paginationQuery.limit,
            eicCode: selectedEicCode,
            paginatedData: paginated,
        };
        const handler = paginated ? handleActivationEventsSuccess : handleDownload;

        RootAfrrService.GetEdgeActivationByArea(params, startLoader, handler, handleError, stopLoader);
    };

    const handleActivationEventsSuccess = ({ data }) => {
        const processedData = _.get(data, ['data', 'data'], []).map((el) => ({
            customerName: _.get(el, ['customerId', 'name'], '--'),
            edgeId: _.get(el, 'deviceId', '--'),
            edgeAlias: _.get(el, 'edgeAlias', '--'),
            instanceName: _.get(el, ['groupId', 'name'], '--'),
            maxSetPoint: _.get(el, 'maxSetPoint', '--'),
            askedAndMaxPower: `${_.get(el, 'maxAskedPower', '--')} / ${_.get(el, 'maxDeliveredPower', '--')}`,
            startTime: momentTimeFormater(el.startTime).format('YYYY-MM-DD HH:mm:ss'),
            endTime: momentTimeFormater(el.endTime).format('YYYY-MM-DD HH:mm:ss'),
            duration: findDiffInSeconds(el.startTime, el.endTime),
            activationStatus: (
                <div className={`${frrClasses.ProfileIconContainer} ${el.status === 'Success' ? frrClasses.StatusSuccess : frrClasses.StatusUnderdelivered}`}>
                    {CamelCaseToTextCase(el.status)}
                </div>
            ),
            info: (
                <div onClick={() => setSignalViewerModal({
                    status: true,
                    data: {
                        ...el,
                        signalId: _.get(el, ['properties', 'signalId'], '--')
                    }
                })}>
                    <CustomTooltip content={'Signal Viewer'}>
                        <UilInfoCircle size={'1.2vw'} style={{ color: 'var(--color-primary)' }} />
                    </CustomTooltip>
                </div>
            ),
        }));

        setData({
            activation: processedData,
            count: _.get(data, ['data', 'count'], 0),
        });
    };

    const handleDownload = ({ data }) => {
        const processedData = _.get(data, ['data', 'data'], []).map((el) => ({
            customerName: _.get(el, ['customerId', 'name'], '--'),
            edgeId: _.get(el, 'deviceId', '--'),
            edgeAlias: _.get(el, 'edgeAlias', '--'),
            instanceName: _.get(el, ['groupId', 'name'], '--'),
            askedAndMaxPower: `${_.get(el, 'maxAskedPower', '--')} / ${_.get(el, 'maxDeliveredPower', '--')}`,
            activationStatus: CamelCaseToTextCase(el.status),
            maxSetPoint: _.get(el, 'maxSetPoint', '--'),
            startTime: momentTimeFormater(el.startTime).format('YYYY-MM-DD HH:mm:ss'),
            endTime: momentTimeFormater(el.endTime).format('YYYY-MM-DD HH:mm:ss'),
            duration: findDiffInSeconds(el.startTime, el.endTime),
        }));

        const headers = [
            'customerName',
            'edgeId',
            'edgeAlias',
            'instanceName',
            'askedAndMaxPower',
            'activationStatus',
            'maxSetPoint',
            'startTime',
            'endTime',
            'duration'
        ];

        processedData?.length > 0 && DownloadAsExcel(processedData, `aFRR Activation - ${selectedArea}`, headers);
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    return (
        <div>
            <div className={AfrrClasses.Filter}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div className={AfrrClasses.FilterContainer}>
                        <div>
                            <div className={classes.FieldControl}>
                                <label>Select EIC Code</label>
                                <DropdownComponent
                                    name="eicCode"
                                    options={eicCodeOptions}
                                    defaultValue={{ value: selectedEicCode }}
                                    onChange={(e) => {
                                        if (e.value !== selectedEicCode) {
                                            setSelectedEicCode(e.value);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div>
                            <div className={classes.FieldControl}>
                                <label>Area</label>
                                <DropdownComponent name="area" options={AREA} defaultValue={{ value: selectedArea }} onChange={(e) => setSelectedArea(e.value)} />
                            </div>
                        </div>
                    </div>
                    <div className={AfrrClasses.FilterContainer}>
                        <div className={AfrrClasses.LeftContainer}>
                            <div className={AfrrClasses.SelecterContainer}>
                                {startDate && endDate && (
                                    <div>
                                        <DateRangePicker
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={(date) => {
                                                setStartDate(moment(date.startDate).toDate());
                                                setEndDate(moment(date.endDate).toDate());
                                            }}
                                            style={{ height: '2.5vw' }}
                                            showLabels={true}
                                        />
                                    </div>
                                )}
                            </div>

                            <div onClick={() => getActivationEvents()}>
                                <button type="submit" className="btn-primary">
                                    Submit
                                </button>
                            </div>
                            <div style={{ marginTop: '2vh' }} onClick={() => getActivationEvents(false)}>
                                <IconComponent icon={UilImport} tooltip={'Download'} size="1.5vw" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={classes.AllDevices}>
                <div className={classes.Header}>
                    <div>
                        <Typography content="Activation chips" />
                        <div className={classes.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={data.count || 0} />
                            </span>
                        </div>
                    </div>
                </div>
                <div>
                    <div>
                        <Table
                            head={[
                                'Customer Name',
                                'Edge ID',
                                'Edge Alias',
                                'Instance Name',
                                'Ask.Power / Max.Power',
                                'Max.Set Point',
                                'Act.Status',
                                'Start Time (CET)',
                                'End Time (CET)',
                                'Duration (Sec)',
                                'Info',
                            ]}
                            keys={[
                                'customerName',
                                'edgeId',
                                'edgeAlias',
                                'instanceName',
                                'askedAndMaxPower',
                                'activationStatus',
                                'maxSetPoint',
                                'startTime',
                                'endTime',
                                'duration',
                                'info'
                            ]}
                            data={data.activation}
                            page={paginationQuery.page}
                            Pagination={true}
                            limit={paginationQuery.limit}
                            handlePageChange={handlePageChange}
                            totalCount={data.count}
                        />
                    </div>
                </div>
            </div>

            <ModalComponent
                isOpen={signalViewerModal.status}
                setOpen={(status) => {
                    setSignalViewerModal({ status: false, data: '' });
                }}
            >
                <MFRRActivationSignalViewer setOpen={setSignalViewerModal} data={signalViewerModal.data} />
            </ModalComponent>
        </div>
    );
};
